import React from "react";
import {isFuture, parseISO} from "date-fns";

import {Text} from "@pg-design/text-module";
import {compact, isEmpty} from "@pg-mono/nodash";
import {dateTimeFormat, formatDate, pluralize, priceFormat, priceM2Format, propertyOffer} from "@pg-mono/string-utils";

import {getParkingPlacesType, ParkingPlacesType} from "../../offer/detail/constants/ParkingPlacesType";
import {IRangeQuery} from "../../request/IRangeQuery";
import {Currency} from "../../types/Currency";
import {getCurrency} from "../../utils/get_currency";
import {VendorType} from "../../vendor/helpers/VendorType";
import {getRoomListTypeMap, RoomListType} from "../helpers/generated_decription/generated_description";
import {IAdditionalArea} from "../interfaces/IAdditionalArea";
import {IParkingPlace} from "../interfaces/IParkingPlace";
import {IRoomAreaLabel} from "../interfaces/IRoomArea";

export enum QuarterType {
    EAST = 1,
    WEST = 2,
    NORTH = 3,
    SOUTH = 4
}
export const getQuarterTypeName = (type: QuarterType) => {
    switch (type) {
        case 1:
            return "east";
        case 2:
            return "west";
        case 3:
            return "north";
        case 4:
            return "south";
    }
};
export const getQuarterTypeNamePL = (type: QuarterType) => {
    switch (type) {
        case QuarterType.EAST:
            return "wschód";
        case QuarterType.WEST:
            return "zachód";
        case QuarterType.NORTH:
            return "północ";
        case QuarterType.SOUTH:
            return "południe";
    }
};

enum KitchenType {
    OPEN = "otwarta",
    CLOSED = "zamknięta"
}

interface IProps {
    additionalAreaList: IAdditionalArea[];
    showDescriptionPrice: boolean;
    kitchenType: string;
    quarterTypeList: string[];
    renderParkingPlaces: string[];
    roomAreaList: IRoomAreaLabel[];
    property: {
        configuration: {
            show_price_m2: boolean;
        };
        floor: number;
        floors: number;
        height_range: {
            lower: number;
            upper: number;
        };
        number: string;
        offer: {
            buildings: number;
            currency: Currency;
            construction_date_range: IRangeQuery<string>;
            floors_above_ground_range: IRangeQuery<number> | null;
            floors_below_ground: number;
            name: string;
            investor: string | null;
            is_holiday_location: boolean;
            is_condohotel: boolean;
            parking_places: IParkingPlace[];
            properties: number;
            stats: {
                properties_count_for_sale: number;
                ranges_area_max: number;
                ranges_area_min: number;
            } | null;
            storage: {
                quantity: number;
                price: number;
            };
            vendor: {
                name: string;
                type: VendorType;
            };
        };
        price: number | null;
        price_m2: number;
        rooms: number;
        quarters: number[];
    };
}

export const PropertyDetailGeneratedDescriptionFlat: React.FunctionComponent<IProps> = (props) => {
    const {property, roomAreaList, quarterTypeList, kitchenType} = props;
    const {offer} = property;
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const sup2 = String.fromCharCode(178);
    const futureEndDate = isFuture(parseISO(offer.construction_date_range.upper));
    const futureStartDate = isFuture(parseISO(offer.construction_date_range.lower));
    const upper = formatDate(offer.construction_date_range.upper, dateTimeFormat.quarter);
    const listQuarterTypesElements = quarterTypeList.map((item) => item);

    const renderParkingPlacesOptions = () => {
        return offer.parking_places.map((parkingPlace: IParkingPlace) => getParkingPlacesType(parkingPlace.type).toLowerCase()).join(", ");
    };

    const roomListTypeMap = React.useMemo(() => getRoomListTypeMap(roomAreaList), [roomAreaList]);

    const basicCollection =
        !isEmpty(roomListTypeMap) &&
        compact([roomListTypeMap[RoomListType.LIVING_ROOM], roomListTypeMap[RoomListType.ROOM], roomListTypeMap[RoomListType.BEDROOM]]);

    const renderBasic = () => {
        return basicCollection && basicCollection.length
            ? basicCollection.map((rooms) => {
                  return (
                      <React.Fragment key={rooms.type}>
                          {rooms.type.toLowerCase()} {rooms.areasString ? ` o powierzchni ${rooms.areasString}` : ""}
                          {", "}
                      </React.Fragment>
                  );
              })
            : null;
    };

    const renderAdditional = () => {
        const additionalCollection =
            !isEmpty(roomListTypeMap) &&
            compact([
                roomListTypeMap[RoomListType.CORRIDOR],
                roomListTypeMap[RoomListType.DINING_ROOM],
                roomListTypeMap[RoomListType.OFFICE],
                roomListTypeMap[RoomListType.WC],
                roomListTypeMap[RoomListType.WARDROBE],
                roomListTypeMap[RoomListType.UTILITY_ROOM],
                roomListTypeMap[RoomListType.ENTRESOL]
            ]);

        return additionalCollection ? (
            <React.Fragment>
                W mieszkaniu {additionalCollection.length > 1 ? "są" : "jest"} także{" "}
                {additionalCollection.map((rooms, index) => {
                    return (
                        <React.Fragment key={rooms.type}>
                            {rooms.type.toLowerCase()} {rooms.areasString ? ` o powierzchni ${rooms.areasString}` : ""}
                            {index === additionalCollection.length - 1 ? "." : ", "}
                        </React.Fragment>
                    );
                })}
            </React.Fragment>
        ) : null;
    };

    return (
        <>
            <Text variant="body_copy_1" mb={2}>
                Cała inwestycja składa się z {offer.buildings} {pluralize(["budynku", "budynków", "budynków"])(offer.buildings)},{" "}
                {pluralize(["w którym", "w których", "w których"])(offer.buildings)}{" "}
                {offer.properties
                    ? `łącznie ${pluralize(["znajduje", "znajdują", "znajduje"])(offer.properties)} się ${offer.properties} ${
                          isApartment
                              ? `${pluralize(["nowy apartament", "nowe apartamenty", "nowych apartamentów"])(offer.properties)}.`
                              : `${pluralize(["nowe mieszkanie", "nowe mieszkania", "nowych mieszkań"])(offer.properties)}.`
                      }`
                    : `znajdują się nowe ${isApartment ? "apartamenty" : "mieszkania"}.`}
            </Text>

            <Text variant="body_copy_1" mb={2}>
                {offer.stats?.properties_count_for_sale
                    ? `Na sprzedaż ${pluralize(["pozostaje", "pozostają", "pozostaje"])(offer.stats.properties_count_for_sale)} ${
                          offer.stats.properties_count_for_sale
                      } ${propertyOffer.nominative(offer.stats.properties_count_for_sale)} ${
                          offer.stats.ranges_area_min || offer.stats.ranges_area_max
                              ? `o powierzchni ${
                                    offer.stats.ranges_area_min !== offer.stats.ranges_area_max
                                        ? `${offer.stats.ranges_area_min && `od ${offer.stats.ranges_area_min}`} ${
                                              offer.stats.ranges_area_max && `do ${offer.stats.ranges_area_max}`
                                          }`
                                        : offer.stats.ranges_area_min
                                } m${sup2}.`
                              : ""
                      }`
                    : "Inwestycja jest już wyprzedana."}
            </Text>

            <Text variant="body_copy_1" mb={2}>
                {offer.floors_above_ground_range
                    ? `W ${pluralize(["budynku", "budynkach", "budynkach"])(offer.buildings)} ${
                          offer.floors_above_ground_range.lower === offer.floors_above_ground_range.upper
                              ? `${pluralize(["jest", "są", "jest"])(offer.floors_above_ground_range.upper)} ${
                                    offer.floors_above_ground_range.upper
                                } ${pluralize(["kondygnacja naziemna", "kondygnacje naziemne", "kondygnacji naziemnych"])(
                                    offer.floors_above_ground_range.upper
                                )}`
                              : `jest od ${offer.floors_above_ground_range.lower} do ${offer.floors_above_ground_range.upper} kondygnacji naziemnych`
                      }`
                    : ""}

                {offer.floors_below_ground
                    ? ` oraz ${offer.floors_below_ground} ${pluralize(["kondygnacja podziemna", "kondygnacje podziemne", "kondygnacji podziemnych"])(
                          offer.floors_below_ground
                      )}.`
                    : ". Budynek nie posiada kondygnacji podziemnych."}
            </Text>

            <Text variant="body_copy_1" mb={2}>
                {futureEndDate || !upper
                    ? `Inwestycja ${offer.name} jest ${futureStartDate ? "w trakcie przygotowań" : "w trakcie budowy"} ${
                          upper && `z planowanym na ${upper} roku terminem oddania do użytkowania`
                      }`
                    : `Inwestycja ${offer.name} została wybudowana i oddana do użytkowania ${upper && `w ${upper} roku`}`}
                .
            </Text>

            <Text variant="body_copy_1" mb={2}>
                {isApartment ? "Nowy apartament" : "Nowe mieszkanie"} o oznaczeniu {property.number}{" "}
                {Number.isInteger(property.floor) && `znajduje się na ${property.floor === 0 ? `parterze` : `${property.floor} piętrze`}`} i jest{" "}
                {property.floors}
                -poziomow{isApartment ? "y" : "e"}. Pomieszczenia mają{" "}
                {property.height_range.lower === property.height_range.upper
                    ? property.height_range.lower
                    : `od ${property.height_range.lower} do ${property.height_range.upper}`}{" "}
                centymetrów wysokości. W {isApartment ? "apartamencie " : "mieszkaniu "} {pluralize(["znajduje", "znajdują", "znajduje"])(property.rooms)} się{" "}
                {property.rooms} {pluralize(["pokój", "pokoje", "pokoi"])(property.rooms)}
                {basicCollection && basicCollection.length ? ": " : ". "}
                {renderBasic()}
                {!!roomListTypeMap[RoomListType.KITCHEN] && (
                    <>
                        {kitchenType.toLowerCase()} {roomListTypeMap[RoomListType.KITCHEN].type.toLowerCase()}
                        {roomListTypeMap[RoomListType.KITCHEN].areasString ? ` o powierzchni ${roomListTypeMap[RoomListType.KITCHEN].areasString}, ` : ""}
                    </>
                )}
                {!!roomListTypeMap[RoomListType.BATHROOM] && (
                    <>
                        {roomListTypeMap[RoomListType.BATHROOM].rooms.length}{" "}
                        {pluralize(["łazienka", "łazienki", "łazienek"])(roomListTypeMap[RoomListType.BATHROOM].rooms.length)}{" "}
                        {roomListTypeMap[RoomListType.BATHROOM].areasString ? ` o powierzchni ${roomListTypeMap[RoomListType.BATHROOM].areasString}. ` : ""}
                    </>
                )}
                {renderAdditional()}
            </Text>

            {!isEmpty(roomListTypeMap) && roomListTypeMap[RoomListType.KITCHEN] && (
                <Text variant="body_copy_1" mb={2}>
                    {kitchenType.toLowerCase() === KitchenType.OPEN &&
                        `Otwarta kuchnia w ${
                            isApartment ? "apartamencie " : "mieszkaniu "
                        } to rozwiązanie, które optycznie powiększa przestrzeń i sprawia, że przyszli właściciele mogą dostosować aranżację do indywidualnych potrzeb i preferencji. Granice między kuchnią a salonem czy pokojem dziennym, może wyznaczać barek lub wyspa kuchenna. Możliwości aranżacyjnych jest naprawdę dużo. Otwarta kuchnia sprzyja też integracji i umożliwia wspólne spędzanie czasu. `}

                    {kitchenType.toLowerCase() === KitchenType.CLOSED &&
                        `Zamknięta kuchnia w ${
                            isApartment ? "apartamencie " : "mieszkaniu "
                        } to rozwiązanie, które izoluje kuchnię od innych pomieszczeń i sprawia, że zapachy nie rozprzestrzeniają się po całym lokalu. Zamknięta kuchnia zapewnia większą dowolność w umiejscowieniu i wyborze sprzętów - zarówno wolnostojących, jak i tych przeznaczonych do zabudowy. Nie wymaga również zachowania spójności kompozycyjnej - zamkniętą kuchnię można urządzić w innym stylu niż pozostałe pomieszczenia.`}
                </Text>
            )}

            {offer.storage && offer.storage.quantity > 0 && (
                <Text variant="body_copy_1" mb={2}>
                    Do {isApartment ? "apartamentu" : "mieszkania"} przewidziano możliwość zakupu komórki lokatorskiej
                    {offer.storage.price &&
                        `, w cenie
                    ${priceFormat(offer.storage.price)}`}
                    , którą można zagospodarować na spiżarnię, warsztat czy składzik.
                </Text>
            )}

            <Text variant="body_copy_1" mb={2}>
                {!isEmpty(property.quarters) && `Okna w ${isApartment ? "apartamencie " : "mieszkaniu "}wychodzą na ${listQuarterTypesElements.join(", ")}.`}
            </Text>

            {property.quarters?.includes(QuarterType.NORTH) && (
                <Text variant="body_copy_1" mb={2}>
                    Północna ekspozycja to idealny wybór dla osób ceniących przytulne i klimatyczne wnętrza. Delikatne, rozproszone światło sprzyja harmonii i
                    stwarza doskonałe warunki do relaksu lub pracy. Okna na północ zapewnią równomierne oświetlenie przez cały dzień.
                </Text>
            )}

            {property.quarters?.includes(QuarterType.SOUTH) && (
                <Text variant="body_copy_1" mb={2}>
                    Okna wychodzące na południe gwarantują maksymalną ilość naturalnego światła, co czyni wnętrza jasnymi i ciepłymi. Dodatkowo południowa
                    ekspozycja pomaga obniżyć koszty ogrzewania w zimie. Okna na południe są idealne dla osób pragnących cieszyć się jasnym otoczeniem, które
                    sprzyja dobremu samopoczuciu oraz pozytywnej energii.
                </Text>
            )}

            {property.quarters?.includes(QuarterType.EAST) && (
                <Text variant="body_copy_1" mb={2}>
                    Wschodnia orientacja zapewnia dostęp do łagodnego porannego światła, idealnego dla rozpoczęcia dnia pełnego energii. Wnętrza pozostają
                    jasne, ale nieprzegrzane, co sprzyja utrzymaniu komfortowej temperatury. Przebywanie w pomieszczeniu z oknami na wschód inspiruje do
                    działania i pozytywnie wpływa na poranny rytuał.
                </Text>
            )}

            {property.quarters?.includes(QuarterType.WEST) && (
                <Text variant="body_copy_1" mb={2}>
                    Zachodnia ekspozycja to gwarancja intensywnego popołudniowego światła, które nadaje wnętrzom przytulności i ciepła. Idealne dla miłośników
                    słonecznych wieczorów, którzy pragną cieszyć się pięknymi zachodami słońca we własnym domu. Zachodnie światło podkreśla kolory i faktury w
                    aranżacji wnętrz, tworząc atmosferę sprzyjającą relaksowi po długim dniu.
                </Text>
            )}

            {!isEmpty(offer.parking_places) ? (
                <>
                    <Text variant="body_copy_1" mb={2}>
                        Dla przyszłych mieszkańców {offer.name} deweloper przewidział {renderParkingPlacesOptions()}.
                    </Text>
                    <Text variant="body_copy_1" mb={2}>
                        Dzięki temu przyszli właściciele nie będą musieli poszukiwać wolnej przestrzeni parkingowej.{" "}
                        {offer.parking_places.map((parkingPlace: IParkingPlace) => {
                            return parkingPlace.type === ParkingPlacesType.UNDERGROUND
                                ? "Dodatkowo, miejsce postojowe w garażu podziemnym zabezpiecza przed niekorzystnymi warunkami atmosferycznymi."
                                : null;
                        })}
                    </Text>
                </>
            ) : (
                <Text variant="body_copy_1" mb={2}>
                    Dla przyszłych mieszkańców {offer.name} deweloper nie przewidział miejsc postojowych.
                </Text>
            )}
            {props.showDescriptionPrice ? (
                <Text variant="body_copy_1" mb={2}>
                    Cena ofertowa {offer.is_condohotel || offer.is_holiday_location ? "apartamentu" : "mieszkania"} wynosi{" "}
                    {priceFormat(property.price as number, {unit: getCurrency({currency: property.offer.currency})})}
                    {property.price_m2 && property.configuration.show_price_m2
                        ? ` (${priceM2Format(property.price_m2, {unit: getCurrency({currency: property.offer.currency})})})`
                        : ""}
                    .
                </Text>
            ) : null}
        </>
    );
};
import React from "react";
import {isFuture, parseISO} from "date-fns";

import {Text} from "@pg-design/text-module";
import {compact, isEmpty} from "@pg-mono/nodash";
import {dateTimeFormat, formatDate, pluralize, priceFormat, priceM2Format, propertyOffer} from "@pg-mono/string-utils";

import {getParkingPlacesType, ParkingPlacesType} from "../../offer/detail/constants/ParkingPlacesType";
import {IRangeQuery} from "../../request/IRangeQuery";
import {Currency} from "../../types/Currency";
import {getCurrency} from "../../utils/get_currency";
import {VendorType} from "../../vendor/helpers/VendorType";
import {getRoomListTypeMap, RoomListType} from "../helpers/generated_decription/generated_description";
import {IAdditionalArea} from "../interfaces/IAdditionalArea";
import {IParkingPlace} from "../interfaces/IParkingPlace";
import {IRoomAreaLabel} from "../interfaces/IRoomArea";

export enum QuarterType {
    EAST = 1,
    WEST = 2,
    NORTH = 3,
    SOUTH = 4
}
export const getQuarterTypeName = (type: QuarterType) => {
    switch (type) {
        case 1:
            return "east";
        case 2:
            return "west";
        case 3:
            return "north";
        case 4:
            return "south";
    }
};
export const getQuarterTypeNamePL = (type: QuarterType) => {
    switch (type) {
        case QuarterType.EAST:
            return "wschód";
        case QuarterType.WEST:
            return "zachód";
        case QuarterType.NORTH:
            return "północ";
        case QuarterType.SOUTH:
            return "południe";
    }
};

enum KitchenType {
    OPEN = "otwarta",
    CLOSED = "zamknięta"
}

interface IProps {
    additionalAreaList: IAdditionalArea[];
    showDescriptionPrice: boolean;
    kitchenType: string;
    quarterTypeList: string[];
    renderParkingPlaces: string[];
    roomAreaList: IRoomAreaLabel[];
    property: {
        configuration: {
            show_price_m2: boolean;
        };
        floor: number;
        floors: number;
        height_range: {
            lower: number;
            upper: number;
        };
        number: string;
        offer: {
            buildings: number;
            currency: Currency;
            construction_date_range: IRangeQuery<string>;
            floors_above_ground_range: IRangeQuery<number> | null;
            floors_below_ground: number;
            name: string;
            investor: string | null;
            is_holiday_location: boolean;
            is_condohotel: boolean;
            parking_places: IParkingPlace[];
            properties: number;
            stats: {
                properties_count_for_sale: number;
                ranges_area_max: number;
                ranges_area_min: number;
            } | null;
            storage: {
                quantity: number;
                price: number;
            };
            vendor: {
                name: string;
                type: VendorType;
            };
        };
        price: number | null;
        price_m2: number;
        rooms: number;
        quarters: number[];
    };
}

export const PropertyDetailGeneratedDescriptionFlat: React.FunctionComponent<IProps> = (props) => {
    const {property, roomAreaList, quarterTypeList, kitchenType} = props;
    const {offer} = property;
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const sup2 = String.fromCharCode(178);
    const futureEndDate = isFuture(parseISO(offer.construction_date_range.upper));
    const futureStartDate = isFuture(parseISO(offer.construction_date_range.lower));
    const upper = formatDate(offer.construction_date_range.upper, dateTimeFormat.quarter);
    const listQuarterTypesElements = quarterTypeList.map((item) => item);

    const renderParkingPlacesOptions = () => {
        return offer.parking_places.map((parkingPlace: IParkingPlace) => getParkingPlacesType(parkingPlace.type).toLowerCase()).join(", ");
    };

    const roomListTypeMap = React.useMemo(() => getRoomListTypeMap(roomAreaList), [roomAreaList]);

    const basicCollection =
        !isEmpty(roomListTypeMap) &&
        compact([roomListTypeMap[RoomListType.LIVING_ROOM], roomListTypeMap[RoomListType.ROOM], roomListTypeMap[RoomListType.BEDROOM]]);

    const renderBasic = () => {
        return basicCollection && basicCollection.length
            ? basicCollection.map((rooms) => {
                  return (
                      <React.Fragment key={rooms.type}>
                          {rooms.type.toLowerCase()} {rooms.areasString ? ` o powierzchni ${rooms.areasString}` : ""}
                          {", "}
                      </React.Fragment>
                  );
              })
            : null;
    };

    const renderAdditional = () => {
        const additionalCollection =
            !isEmpty(roomListTypeMap) &&
            compact([
                roomListTypeMap[RoomListType.CORRIDOR],
                roomListTypeMap[RoomListType.DINING_ROOM],
                roomListTypeMap[RoomListType.OFFICE],
                roomListTypeMap[RoomListType.WC],
                roomListTypeMap[RoomListType.WARDROBE],
                roomListTypeMap[RoomListType.UTILITY_ROOM],
                roomListTypeMap[RoomListType.ENTRESOL]
            ]);

        return additionalCollection ? (
            <React.Fragment>
                W mieszkaniu {additionalCollection.length > 1 ? "są" : "jest"} także{" "}
                {additionalCollection.map((rooms, index) => {
                    return (
                        <React.Fragment key={rooms.type}>
                            {rooms.type.toLowerCase()} {rooms.areasString ? ` o powierzchni ${rooms.areasString}` : ""}
                            {index === additionalCollection.length - 1 ? "." : ", "}
                        </React.Fragment>
                    );
                })}
            </React.Fragment>
        ) : null;
    };

    return (
        <>
            <Text variant="body_copy_1" mb={2}>
                Cała inwestycja składa się z {offer.buildings} {pluralize(["budynku", "budynków", "budynków"])(offer.buildings)},{" "}
                {pluralize(["w którym", "w których", "w których"])(offer.buildings)}{" "}
                {offer.properties
                    ? `łącznie ${pluralize(["znajduje", "znajdują", "znajduje"])(offer.properties)} się ${offer.properties} ${
                          isApartment
                              ? `${pluralize(["nowy apartament", "nowe apartamenty", "nowych apartamentów"])(offer.properties)}.`
                              : `${pluralize(["nowe mieszkanie", "nowe mieszkania", "nowych mieszkań"])(offer.properties)}.`
                      }`
                    : `znajdują się nowe ${isApartment ? "apartamenty" : "mieszkania"}.`}
            </Text>

            <Text variant="body_copy_1" mb={2}>
                {offer.stats?.properties_count_for_sale
                    ? `Na sprzedaż ${pluralize(["pozostaje", "pozostają", "pozostaje"])(offer.stats.properties_count_for_sale)} ${
                          offer.stats.properties_count_for_sale
                      } ${propertyOffer.nominative(offer.stats.properties_count_for_sale)} ${
                          offer.stats.ranges_area_min || offer.stats.ranges_area_max
                              ? `o powierzchni ${
                                    offer.stats.ranges_area_min !== offer.stats.ranges_area_max
                                        ? `${offer.stats.ranges_area_min && `od ${offer.stats.ranges_area_min}`} ${
                                              offer.stats.ranges_area_max && `do ${offer.stats.ranges_area_max}`
                                          }`
                                        : offer.stats.ranges_area_min
                                } m${sup2}.`
                              : ""
                      }`
                    : "Inwestycja jest już wyprzedana."}
            </Text>

            <Text variant="body_copy_1" mb={2}>
                {offer.floors_above_ground_range
                    ? `W ${pluralize(["budynku", "budynkach", "budynkach"])(offer.buildings)} ${
                          offer.floors_above_ground_range.lower === offer.floors_above_ground_range.upper
                              ? `${pluralize(["jest", "są", "jest"])(offer.floors_above_ground_range.upper)} ${
                                    offer.floors_above_ground_range.upper
                                } ${pluralize(["kondygnacja naziemna", "kondygnacje naziemne", "kondygnacji naziemnych"])(
                                    offer.floors_above_ground_range.upper
                                )}`
                              : `jest od ${offer.floors_above_ground_range.lower} do ${offer.floors_above_ground_range.upper} kondygnacji naziemnych`
                      }`
                    : ""}

                {offer.floors_below_ground
                    ? ` oraz ${offer.floors_below_ground} ${pluralize(["kondygnacja podziemna", "kondygnacje podziemne", "kondygnacji podziemnych"])(
                          offer.floors_below_ground
                      )}.`
                    : ". Budynek nie posiada kondygnacji podziemnych."}
            </Text>

            <Text variant="body_copy_1" mb={2}>
                {futureEndDate || !upper
                    ? `Inwestycja ${offer.name} jest ${futureStartDate ? "w trakcie przygotowań" : "w trakcie budowy"} ${
                          upper && `z planowanym na ${upper} roku terminem oddania do użytkowania`
                      }`
                    : `Inwestycja ${offer.name} została wybudowana i oddana do użytkowania ${upper && `w ${upper} roku`}`}
                .
            </Text>

            <Text variant="body_copy_1" mb={2}>
                {isApartment ? "Nowy apartament" : "Nowe mieszkanie"} o oznaczeniu {property.number}{" "}
                {Number.isInteger(property.floor) && `znajduje się na ${property.floor === 0 ? `parterze` : `${property.floor} piętrze`}`} i jest{" "}
                {property.floors}
                -poziomow{isApartment ? "y" : "e"}. Pomieszczenia mają{" "}
                {property.height_range.lower === property.height_range.upper
                    ? property.height_range.lower
                    : `od ${property.height_range.lower} do ${property.height_range.upper}`}{" "}
                centymetrów wysokości. W {isApartment ? "apartamencie " : "mieszkaniu "} {pluralize(["znajduje", "znajdują", "znajduje"])(property.rooms)} się{" "}
                {property.rooms} {pluralize(["pokój", "pokoje", "pokoi"])(property.rooms)}
                {basicCollection && basicCollection.length ? ": " : ". "}
                {renderBasic()}
                {!!roomListTypeMap[RoomListType.KITCHEN] && (
                    <>
                        {kitchenType.toLowerCase()} {roomListTypeMap[RoomListType.KITCHEN].type.toLowerCase()}
                        {roomListTypeMap[RoomListType.KITCHEN].areasString ? ` o powierzchni ${roomListTypeMap[RoomListType.KITCHEN].areasString}, ` : ""}
                    </>
                )}
                {!!roomListTypeMap[RoomListType.BATHROOM] && (
                    <>
                        {roomListTypeMap[RoomListType.BATHROOM].rooms.length}{" "}
                        {pluralize(["łazienka", "łazienki", "łazienek"])(roomListTypeMap[RoomListType.BATHROOM].rooms.length)}{" "}
                        {roomListTypeMap[RoomListType.BATHROOM].areasString ? ` o powierzchni ${roomListTypeMap[RoomListType.BATHROOM].areasString}. ` : ""}
                    </>
                )}
                {renderAdditional()}
            </Text>

            {!isEmpty(roomListTypeMap) && roomListTypeMap[RoomListType.KITCHEN] && (
                <Text variant="body_copy_1" mb={2}>
                    {kitchenType.toLowerCase() === KitchenType.OPEN &&
                        `Otwarta kuchnia w ${
                            isApartment ? "apartamencie " : "mieszkaniu "
                        } to rozwiązanie, które optycznie powiększa przestrzeń i sprawia, że przyszli właściciele mogą dostosować aranżację do indywidualnych potrzeb i preferencji. Granice między kuchnią a salonem czy pokojem dziennym, może wyznaczać barek lub wyspa kuchenna. Możliwości aranżacyjnych jest naprawdę dużo. Otwarta kuchnia sprzyja też integracji i umożliwia wspólne spędzanie czasu. `}

                    {kitchenType.toLowerCase() === KitchenType.CLOSED &&
                        `Zamknięta kuchnia w ${
                            isApartment ? "apartamencie " : "mieszkaniu "
                        } to rozwiązanie, które izoluje kuchnię od innych pomieszczeń i sprawia, że zapachy nie rozprzestrzeniają się po całym lokalu. Zamknięta kuchnia zapewnia większą dowolność w umiejscowieniu i wyborze sprzętów - zarówno wolnostojących, jak i tych przeznaczonych do zabudowy. Nie wymaga również zachowania spójności kompozycyjnej - zamkniętą kuchnię można urządzić w innym stylu niż pozostałe pomieszczenia.`}
                </Text>
            )}

            {offer.storage && offer.storage.quantity > 0 && (
                <Text variant="body_copy_1" mb={2}>
                    Do {isApartment ? "apartamentu" : "mieszkania"} przewidziano możliwość zakupu komórki lokatorskiej
                    {offer.storage.price &&
                        `, w cenie
                    ${priceFormat(offer.storage.price)}`}
                    , którą można zagospodarować na spiżarnię, warsztat czy składzik.
                </Text>
            )}

            <Text variant="body_copy_1" mb={2}>
                {!isEmpty(property.quarters) && `Okna w ${isApartment ? "apartamencie " : "mieszkaniu "}wychodzą na ${listQuarterTypesElements.join(", ")}.`}
            </Text>

            {property.quarters?.includes(QuarterType.NORTH) && (
                <Text variant="body_copy_1" mb={2}>
                    Północna ekspozycja to idealny wybór dla osób ceniących przytulne i klimatyczne wnętrza. Delikatne, rozproszone światło sprzyja harmonii i
                    stwarza doskonałe warunki do relaksu lub pracy. Okna na północ zapewnią równomierne oświetlenie przez cały dzień.
                </Text>
            )}

            {property.quarters?.includes(QuarterType.SOUTH) && (
                <Text variant="body_copy_1" mb={2}>
                    Okna wychodzące na południe gwarantują maksymalną ilość naturalnego światła, co czyni wnętrza jasnymi i ciepłymi. Dodatkowo południowa
                    ekspozycja pomaga obniżyć koszty ogrzewania w zimie. Okna na południe są idealne dla osób pragnących cieszyć się jasnym otoczeniem, które
                    sprzyja dobremu samopoczuciu oraz pozytywnej energii.
                </Text>
            )}

            {property.quarters?.includes(QuarterType.EAST) && (
                <Text variant="body_copy_1" mb={2}>
                    Wschodnia orientacja zapewnia dostęp do łagodnego porannego światła, idealnego dla rozpoczęcia dnia pełnego energii. Wnętrza pozostają
                    jasne, ale nieprzegrzane, co sprzyja utrzymaniu komfortowej temperatury. Przebywanie w pomieszczeniu z oknami na wschód inspiruje do
                    działania i pozytywnie wpływa na poranny rytuał.
                </Text>
            )}

            {property.quarters?.includes(QuarterType.WEST) && (
                <Text variant="body_copy_1" mb={2}>
                    Zachodnia ekspozycja to gwarancja intensywnego popołudniowego światła, które nadaje wnętrzom przytulności i ciepła. Idealne dla miłośników
                    słonecznych wieczorów, którzy pragną cieszyć się pięknymi zachodami słońca we własnym domu. Zachodnie światło podkreśla kolory i faktury w
                    aranżacji wnętrz, tworząc atmosferę sprzyjającą relaksowi po długim dniu.
                </Text>
            )}

            {!isEmpty(offer.parking_places) ? (
                <>
                    <Text variant="body_copy_1" mb={2}>
                        Dla przyszłych mieszkańców {offer.name} deweloper przewidział {renderParkingPlacesOptions()}.
                    </Text>
                    <Text variant="body_copy_1" mb={2}>
                        Dzięki temu przyszli właściciele nie będą musieli poszukiwać wolnej przestrzeni parkingowej.{" "}
                        {offer.parking_places.map((parkingPlace: IParkingPlace) => {
                            return parkingPlace.type === ParkingPlacesType.UNDERGROUND
                                ? "Dodatkowo, miejsce postojowe w garażu podziemnym zabezpiecza przed niekorzystnymi warunkami atmosferycznymi."
                                : null;
                        })}
                    </Text>
                </>
            ) : (
                <Text variant="body_copy_1" mb={2}>
                    Dla przyszłych mieszkańców {offer.name} deweloper nie przewidział miejsc postojowych.
                </Text>
            )}
            {props.showDescriptionPrice ? (
                <Text variant="body_copy_1" mb={2}>
                    Cena ofertowa {offer.is_condohotel || offer.is_holiday_location ? "apartamentu" : "mieszkania"} wynosi{" "}
                    {priceFormat(property.price as number, {unit: getCurrency({currency: property.offer.currency})})}
                    {property.price_m2 && property.configuration.show_price_m2
                        ? ` (${priceM2Format(property.price_m2, {unit: getCurrency({currency: property.offer.currency})})})`
                        : ""}
                    .
                </Text>
            ) : null}
        </>
    );
};
